/**
 * Die Auftragsinfo, wie sie aussieht - einmal für die Akte und für den
 * Wochenplan.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „Wochenplan: Popup am Auftrag zeigt die Auftragsinfo anders (zweimal
 *    «System», keine Bilder) als die Akte."
 *
 * ZWEI ABSCHRIFTEN DERSELBEN ANZEIGE LAUFEN AUSEINANDER, sobald jemand eine
 * davon nachzieht - und genau das ist passiert: das Gesicht, der kurze Name
 * und die Übersetzung der Quelle sind an der Akte nachgezogen worden, am
 * Popup nicht. Die ABFRAGE dahinter steht seit dieser Version ebenfalls
 * einmal (`api/src/auftragsinfo.ts`); nur beides zusammen behebt es.
 *
 * WAS DIE BEIDEN BILDSCHIRME UNTERSCHEIDET, STEHT ALS EIGENSCHAFT DA und
 * nicht als zweite Fassung: wie viele Zeilen zu sehen sind, und ob neben
 * einer Zeile die Handgriffe stehen. Der Wochenplan ist kein Archiv - wer
 * den ganzen Verlauf will, geht in die Akte.
 */

import { datumKurz, zeitpunkt } from '../api.ts';
import { Gesicht, kurzeNamen } from './Person.tsx';
import { quelleDaneben, werSteht } from './ereignisquelle.ts';

export interface Infozeile {
  id?: string;
  zeitpunkt: string;
  quelle: string;
  verfasser_name: string | null;
  text: string;
  benutzer_id?: string | null;
  bearbeitet_am?: string | null;
  bearbeitet_name?: string | null;
  gesicht_id?: string | null;
  hat_bild?: boolean;
  /** .585: ein Anhang zum Öffnen (zum Beispiel der Aushang einer gesendeten Mail). */
  anhang?: { href: string; name: string };
}

export default function Auftragsinfoliste({
  zeilen, deckel = 40, aufBerichtigen, aufLoeschen, darfEintrag,
}: {
  zeilen: Infozeile[];
  deckel?: number;
  /** Ohne diese drei steht die Liste nur da - so im Wochenplan. */
  aufBerichtigen?: (e: Infozeile) => void;
  aufLoeschen?: (e: Infozeile) => void;
  darfEintrag?: (e: Infozeile) => boolean;
}) {
  /* NUR DER VORNAME - UND DER VOLLE NAME, WENN ER SEIN MUSS.
     Gerechnet über die GANZE Liste und nicht je Zeile: ob ein Vorname
     ausreicht, hängt daran, wer sonst noch in dieser Liste steht. Der
     Berichtigende geht mit ein - er steht in derselben Zeile, und zwei
     Regeln für eine Zeile wären eine zu viel. */
  const namen = kurzeNamen([
    ...zeilen.map((e) => ({ name: e.verfasser_name, wer: e.gesicht_id ?? null })),
    ...zeilen.map((e) => ({ name: e.bearbeitet_name ?? null, wer: null })),
  ]);
  const kurz = (n: string) => namen.get(n.trim()) ?? n;
  const handgriffe = !!(aufBerichtigen && aufLoeschen && darfEintrag);

  return (
    <ul className="verlauf">
      {zeilen.slice(0, deckel).map((e, i) => {
        /* DIE QUELLE STEHT NUR DANEBEN, WENN SIE ETWAS HINZUFÜGT. Ohne
           diesen Riegel stand im Popup „System, system" - der Name kommt
           bei einem Eintrag ohne Verfasser aus derselben Quelle. */
        const dazu = quelleDaneben(e.verfasser_name, e.quelle);
        return (
          <li key={e.id ?? i}>
            <span className="wann">{zeitpunkt(e.zeitpunkt)}</span>
            {/* DAS GESICHT STEHT VOR DEM NAMEN UND NICHT STATT SEINER. Ein
                Bild von 22 Punkten beantwortet „von wem ist das" schnell und
                nicht sicher; wer nur das Bild zeigt, verliert die Auskunft,
                sobald zwei Menschen ähnlich aussehen. Ohne hochgeladenes
                Bild stehen die Initialen da - und bei einem Eintrag der
                Automatik, der zu Recht keinen Verfasser hat, gar kein Kreis. */}
            <span className="wer">
              {e.verfasser_name && (
                <Gesicht klein benutzerId={e.gesicht_id ?? null}
                         name={e.verfasser_name}
                         hatBild={!!e.hat_bild} />
              )}
              <span className="wername">
                {werSteht(e.verfasser_name, e.quelle, kurz)}
                {dazu && <span className="leise">, {dazu}</span>}
              </span>
            </span>
            <span>
              {e.text}
              {e.anhang && <> <a className="kartenweg" href={e.anhang.href} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>{e.anhang.name}</a></>}
              {/* BERICHTIGT HEISST SICHTBAR BERICHTIGT. Ein Eintrag, der
                  sich unbemerkt ändern lässt, ist keine Spur mehr, sondern
                  eine Behauptung. */}
              {e.bearbeitet_am && (
                <span className="leise klein">
                  {', berichtigt '}{datumKurz(e.bearbeitet_am)}
                  {e.bearbeitet_name ? ` von ${kurz(e.bearbeitet_name)}` : ''}
                </span>
              )}
              {handgriffe && darfEintrag!(e) && (
                <>
                  <button className="kartenweg" style={{ marginLeft: 8 }}
                          onClick={() => aufBerichtigen!(e)}>
                    berichtigen
                  </button>
                  <button className="kartenweg" style={{ marginLeft: 6 }}
                          onClick={() => aufLoeschen!(e)}>
                    löschen
                  </button>
                </>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
